'use client'

import { trackMetaPixelEvent } from '@/components/MetaPixel'
import { InfiniteMovingCards } from '@/components/ui/infinite-moving-cards'
import { featuredTemplates, localDevAgentId } from '@/data/templates/featuredTemplates'
import { usePricingModal } from '@/hooks/usePricingModal'
import { analytics } from '@/lib/analytics'
import { forkAgent } from '@/lib/api'
import { useGetUserQuery, useGetUserQuotaQuery } from '@/lib/features/user/userApi'
import { PAGE_PATH } from '@/lib/page-paths'
import { hasEnoughAppCount, nextTierId } from '@/lib/subscription-utils'
import { useRouter } from 'next/navigation'
import { useCallback, useState } from 'react'
import { toast } from 'react-toastify'
import { ApiAgentForkResponse } from 'sprites-types/api/agents'

const InfiniteCarousel = () => {
  const [isLoading, setIsLoading] = useState(false)
  const router = useRouter()
  const { data: userData } = useGetUserQuery()
  const { data: quota } = useGetUserQuotaQuery()
  const workspace = userData?.workspace
  const user = userData?.user
  const { setOpen: setOpenPricingModal } = usePricingModal()
  const isDevMode = process.env.NODE_ENV === 'development' && !!localDevAgentId

  const handleStartClick = useCallback(
    async (agentId: string, title: string) => {
      setIsLoading(true)

      if (!workspace?.id) {
        // note(adam): user is trying to click a tile before they are logged in, so we just log and return.
        // eventually, they will get an anonymous user + workspace, and be able to click
        console.warn(`Workspace not found ${workspace} user: ${user}`)
        return
      }

      if (!hasEnoughAppCount(quota)) {
        setOpenPricingModal({ highlightedPlanId: nextTierId(quota) })
        setIsLoading(false)
        return
      }

      try {
        console.log('AgentTemplates: forking agent from agent id', agentId)
        const response: ApiAgentForkResponse = await forkAgent(workspace.id, agentId)

        const doProfiling = false
        const url = `${PAGE_PATH.AGENTS}/${response.agent.id}${doProfiling ? '?profiling=true' : ''}`

        router.push(url)
        console.log('AgentTemplates: pushing to', url)

        // Track template click in Meta Pixel
        trackMetaPixelEvent('ViewContent', {
          content_type: 'template',
          content_id: agentId,
          content_name: title,
        })

        analytics.appLifecycle.appForked(
          response.agent.id,
          workspace.id,
          agentId,
          response.agent.name
        )
        analytics.landingPage.templateClicked(agentId, title)
      } catch (error) {
        console.error('Error forking agent:', error)
        setIsLoading(false)

        toast.error('Failed to load, please try again')
      }
    },
    [router, user, workspace]
  )

  const slideData = featuredTemplates.map((template) => ({
    title: template.displayName || '',
    src: template.backgroundImage || '',
    agentId: isDevMode && localDevAgentId ? localDevAgentId : template.agentId,
  }))

  return (
    <div className="relative w-full max-w-[100vw] overflow-hidden">
      <InfiniteMovingCards
        className="w-full max-w-[100vw]"
        items={slideData}
        direction="left"
        speed="slow"
        isLoading={isLoading}
        onStartClick={handleStartClick}
      />
    </div>
  )
}

export default InfiniteCarousel
